import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Platform, Dimensions, ScrollView, Image } from 'react-native';
import { YStack, XStack, Button, SizableText, TextArea, H3, Separator, useTheme } from 'tamagui';
import { View, Text } from '@constants/Themed';
import DefaultModal from 'react-native-modal';
import { AntDesign, FontAwesome6 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import apis from '@api/api';
import { store } from '@reduxLocal/persistState';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  isDarkTheme?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showFeedbackModal: boolean;
  userFeedback: string;
  sendingFeedback: boolean;
}

interface ErrorLocation {
  component: string;
  file: string;
  line?: number;
  column?: number;
  stack: string;
}

class CrashInterceptor extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showFeedbackModal: false,
      userFeedback: '',
      sendingFeedback: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console for development
    console.error('CrashInterceptor caught an error:', error, errorInfo);

    // Expose global error reporting function
    if (Platform.OS === 'web') {
      (window as any).__CRASH_INTERCEPTOR_REPORT__ = this.reportError;
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Auto-report critical errors in production
    if (Platform.OS === 'web' && process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo);
    }
  }

  private parseErrorLocation = (error: Error, errorInfo: ErrorInfo): ErrorLocation => {
    const stack = error.stack || '';
    const componentStack = errorInfo.componentStack || '';
    
    // Try to extract file and line information from stack trace
    const stackLines = stack.split('\n');
    const firstRelevantLine = stackLines.find(line => 
      line.includes('.tsx') || line.includes('.ts') || line.includes('.js') || line.includes('.jsx')
    );

    let file = 'Unknown';
    let line: number | undefined;
    let column: number | undefined;

    if (firstRelevantLine) {
      // Extract file path, line, and column from stack trace
      const match = firstRelevantLine.match(/at\s+.*?\s+\((.+?):(\d+):(\d+)\)|(.+?):(\d+):(\d+)/);
      if (match) {
        file = match[1] || match[4] || 'Unknown';
        line = parseInt(match[2] || match[5] || '0');
        column = parseInt(match[3] || match[6] || '0');
      }
    }

    // Extract component name from component stack
    const componentMatch = componentStack.match(/in\s+(\w+)/);
    const component = componentMatch ? componentMatch[1] : 'Unknown';

    return {
      component,
      file: file.split('/').pop() || file, // Get just filename
      line,
      column,
      stack: componentStack,
    };
  };

  private reportError = async (error: Error, errorInfo?: ErrorInfo, userFeedback?: string) => {
    try {
      const errorLocation = this.parseErrorLocation(error, errorInfo || { componentStack: '' });
      
      const errorReport = {
        message: error.message,
        name: error.name,
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        location: errorLocation,
        userFeedback: userFeedback || '',
        timestamp: new Date().toISOString(),
        platform: Platform.OS,
        userAgent: Platform.OS === 'web' ? navigator.userAgent : undefined,
        url: Platform.OS === 'web' ? window.location.href : undefined,
        appVersion: '1.0.0', // You can get this from your app config
      };

      // Send to your error reporting service
      await apis.reportError(errorReport);
      
      return true;
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
      return false;
    }
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showFeedbackModal: false,
      userFeedback: '',
      sendingFeedback: false,
    });
  };

  private handleShowFeedback = () => {
    this.setState({ showFeedbackModal: true });
  };

  private handleSendFeedback = async () => {
    const { error, errorInfo, userFeedback } = this.state;
    
    if (!error) return;

    this.setState({ sendingFeedback: true });

    const success = await this.reportError(error, errorInfo, userFeedback);
    
    this.setState({ sendingFeedback: false });

    if (success) {
      Alert.alert(
        'Thank you!',
        'Your feedback has been sent. Our team will investigate this issue.',
        [{ text: 'OK', onPress: this.handleRetry }]
      );
    } else {
      Alert.alert(
        'Error',
        'Failed to send feedback. Please try again later.',
        [{ text: 'OK' }]
      );
    }
  };

  private handleCloseFeedback = () => {
    this.setState({ 
      showFeedbackModal: false,
      userFeedback: '' 
    });
  };

  private getResponsiveStyles = () => {
    const windowDimensions = Dimensions.get('window');
    const { width, height } = windowDimensions;

    const isMobile = width < 768;
    const isTablet = width >= 768 && width < 1024;
    const isDesktop = width >= 1024;
    const isShortScreen = height < 600;

    const getScaleFactor = () => {
      if (isMobile && isShortScreen) return 0.75;
      if (isMobile) return 0.85;
      if (isTablet) return 0.95;
      return 1;
    };

    const scaleFactor = getScaleFactor();

    return {
      spacing: {
        xs: Math.max(4, 8 * scaleFactor),
        sm: Math.max(8, 12 * scaleFactor),
        md: Math.max(12, 16 * scaleFactor),
        lg: Math.max(16, 24 * scaleFactor),
        xl: Math.max(24, 32 * scaleFactor),
        xxl: Math.max(32, 48 * scaleFactor),
      },
      typography: {
        xs: Math.max(10, 12 * scaleFactor),
        sm: Math.max(12, 14 * scaleFactor),
        base: Math.max(14, 16 * scaleFactor),
        lg: Math.max(16, 18 * scaleFactor),
        xl: Math.max(18, 20 * scaleFactor),
        xxl: Math.max(20, 24 * scaleFactor),
      },
      components: {
        cardRadius: isMobile ? 12 : 16,
        buttonHeight: Math.max(44, 48 * scaleFactor),
        iconSize: Math.max(20, 24 * scaleFactor),
      },
      isMobile,
      isTablet,
      isDesktop,
    };
  };

  private renderErrorDetails = () => {
    const { error, errorInfo } = this.state;
    if (!error || !errorInfo) return null;

    const errorLocation = this.parseErrorLocation(error, errorInfo);
    const responsive = this.getResponsiveStyles();

    return (
      <YStack
        space="$3"
        padding={responsive.spacing.lg}
        backgroundColor="#F9FAFB"
        borderRadius={responsive.components.cardRadius}
        borderWidth={1}
        borderColor="#E5E7EB"
        {...Platform.select({
          web: {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }
        })}
      >
        <H3
          color="#EF4444"
          fontSize={responsive.typography.lg}
          fontWeight="700"
        >
          Error Details
        </H3>
        
        <YStack space="$2">
          <SizableText size="$3" fontWeight="bold" color="#1F2937">
            Location:
          </SizableText>
          <SizableText size="$2" color="#4B5563">
            Component: {errorLocation.component}
          </SizableText>
          <SizableText size="$2" color="#4B5563">
            File: {errorLocation.file}
            {errorLocation.line && `:${errorLocation.line}`}
            {errorLocation.column && `:${errorLocation.column}`}
          </SizableText>
        </YStack>

        <YStack space="$2">
          <SizableText size="$3" fontWeight="bold" color="#1F2937">
            Error:
          </SizableText>
          <SizableText size="$2" color="#EF4444">
            {error.name}: {error.message}
          </SizableText>
        </YStack>

        {__DEV__ && (
          <YStack space="$2">
            <SizableText size="$3" fontWeight="bold" color="#1F2937">
              Stack Trace:
            </SizableText>
            <ScrollView
              style={{
                maxHeight: 200,
                backgroundColor: '#FFFFFF',
                borderRadius: 8,
                padding: responsive.spacing.sm,
                borderWidth: 1,
                borderColor: '#E5E7EB',
              }}
            >
              <SizableText size="$1" fontFamily="$mono" color="#4B5563">
                {error.stack}
              </SizableText>
            </ScrollView>
          </YStack>
        )}
      </YStack>
    );
  };

  private renderFeedbackModal = () => {
    const { showFeedbackModal, userFeedback, sendingFeedback } = this.state;
    const responsive = this.getResponsiveStyles();

    return (
      <DefaultModal
        isVisible={showFeedbackModal}
        onBackdropPress={this.handleCloseFeedback}
        animationIn="slideInUp"
        animationOut="slideOutDown"
        style={{ justifyContent: 'center', alignItems: 'center' }}
      >
        <YStack
          backgroundColor="#FFFFFF"
          padding={responsive.spacing.xl}
          borderRadius={responsive.components.cardRadius}
          space="$4"
          minWidth={responsive.isMobile ? 300 : 400}
          maxWidth={500}
          borderWidth={1}
          borderColor="#E5E7EB"
          zIndex={2000}
          {...Platform.select({
            web: {
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }
          })}
        >
          <XStack justifyContent="space-between" alignItems="center">
            <H3 color="#1F2937" fontSize={responsive.typography.lg}>
              Send Feedback
            </H3>
            <Button
              size="$2"
              circular
              onPress={this.handleCloseFeedback}
              backgroundColor="transparent"
              pressStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
            >
              <AntDesign name="close" size={20} color="#6B7280" />
            </Button>
          </XStack>

          <Separator borderColor="#E5E7EB" />

          <SizableText size="$3" color="#4B5563">
            Help us improve by describing what you were doing when this error occurred:
          </SizableText>

          <TextArea
            placeholder="Describe what happened, what you expected, and any steps to reproduce..."
            placeholderTextColor="#9CA3AF"
            value={userFeedback}
            onChangeText={(text) => this.setState({ userFeedback: text })}
            minHeight={100}
            backgroundColor="#F9FAFB"
            borderColor="#E5E7EB"
            color="#1F2937"
          />

          <XStack space="$3" justifyContent="flex-end">
            <Button
              variant="outlined"
              onPress={this.handleCloseFeedback}
              disabled={sendingFeedback}
              borderColor="#E5E7EB"
              color="#4B5563"
            >
              Cancel
            </Button>
            <Button
              onPress={this.handleSendFeedback}
              disabled={sendingFeedback || !userFeedback.trim()}
              backgroundColor="#3B82F6"
              color="white"
              pressStyle={{ backgroundColor: '#2563EB' }}
            >
              {sendingFeedback ? 'Sending...' : 'Send Feedback'}
            </Button>
          </XStack>
        </YStack>
      </DefaultModal>
    );
  };

  render() {
    const { hasError, error } = this.state;
    const { isDarkTheme } = this.props;
    const responsive = this.getResponsiveStyles();
    const theme = !isDarkTheme;
    const windowHeight = Dimensions.get('window').height;

    if (hasError && error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <View style={{
          flex: 1,
          minHeight: Platform.OS === 'web' ? Math.max(windowHeight, 600) : windowHeight,
          position: 'relative' as any,
          ...Platform.select({
            web: {
              overflow: 'hidden' as any,
              justifyContent: 'center' as any,
              alignItems: 'center' as any,
            }
          })
        } as any}>
          {/* Modern Gradient Background - Matching LoginScreen */}
          <LinearGradient
            colors={['#C8A2C8', '#87CEEB']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: 'none' as any,
            }}
          />

          {/* Hero Background Image - Web Only */}
          {Platform.OS === 'web' && (
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 0,
                pointerEvents: 'none' as any,
              }}
            >
              <Image
                source={require('@assets/backgrounds/hero-bg.png')}
                style={{
                  width: '100%',
                  height: '100%',
                  opacity: 0.4,
                  resizeMode: 'contain',
                }}
              />
            </View>
          )}

          {/* Additional gradient overlay */}
          {Platform.OS === 'web' && (
            <View style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              zIndex: 1,
              pointerEvents: 'none' as any,
            }} />
          )}

          {/* Cityscape Overlay - Web Only */}
          {Platform.OS === 'web' && (
            <View
              style={{
                position: 'absolute',
                bottom: -70,
                left: 0,
                right: 0,
                height: 256,
                width: '105%',
                zIndex: 1,
                marginHorizontal: -30,
                pointerEvents: 'none' as any,
              }}
            >
              <Image
                source={require('@assets/backgrounds/cityscape-large.png')}
                style={{
                  width: '100%',
                  height: '100%',
                  opacity: 0.9,
                  resizeMode: 'contain',
                }}
              />
            </View>
          )}

          {/* Main Content Container */}
          <ScrollView
            contentContainerStyle={{
              flexGrow: 1,
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: Platform.OS === 'web' ? Math.max(windowHeight, 600) : windowHeight,
              padding: responsive.spacing.xl,
              position: 'relative' as any,
            } as any}
            style={{
              flex: 1,
              minHeight: Platform.OS === 'web'
                ? (() => {
                  const webScale = (store.getState() as any)?.app?.webScale ?? 1;
                  return webScale !== 1 ? `${100 / webScale}dvh` : "100dvh";
                })()
                : "100dvh",
              zIndex: 1000,
              position: 'relative' as any,
            } as any}
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            {/* Content Card Container */}
            <View style={{
              backgroundColor: '#FFFFFF',
              borderRadius: 24,
              padding: responsive.isMobile ? responsive.spacing.lg : responsive.spacing.xl,
              width: '100%',
              minWidth: responsive.isMobile ? 320 : 500,
              maxWidth: 700,
              borderWidth: 1,
              borderColor: '#E5E7EB',
              alignSelf: 'center',
              zIndex: 1001,
              maxHeight: "92vh",
              position: 'relative' as any,
              ...Platform.select({
                web: {
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                }
              }),
            } as any}>
              <YStack maxHeight="80vh" space="$4" alignItems="center">
                {/* Error Icon with Background */}
                <View style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: 50,
                  padding: responsive.spacing.lg,
                  marginBottom: responsive.spacing.sm,
                }}>
                  <FontAwesome6
                    name="triangle-exclamation"
                    size={responsive.isMobile ? 48 : 64}
                    color="#EF4444"
                  />
                </View>

                <H3
                  textAlign="center"
                  color="#1F2937"
                  fontSize={responsive.typography.xxl}
                  fontWeight="700"
                >
                  Oops! Something went wrong
                </H3>

                <SizableText
                  size="$4"
                  textAlign="center"
                  color="#4B5563"
                  lineHeight={responsive.typography.base * 1.5}
                  maxWidth={500}
                >
                  We're sorry for the inconvenience. The app encountered an unexpected error.
                </SizableText>

                {__DEV__ && this.renderErrorDetails()}

                <XStack
                  space="$3"
                  flexWrap="wrap"
                  justifyContent="center"
                  width="100%"
                  paddingTop={responsive.spacing.md}
                >
                  <Button
                    variant="outlined"
                    onPress={this.handleShowFeedback}
                    borderColor="#E5E7EB"
                    color="#4B5563"
                    backgroundColor="transparent"
                    pressStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                    icon={<FontAwesome6 name="comment-dots" size={20} color="#4B5563" />}
                    minWidth={responsive.isMobile ? 120 : 150}
                  >
                    Send Feedback
                  </Button>

                  <Button
                    onPress={this.handleRetry}
                    backgroundColor="#3B82F6"
                    color="white"
                    pressStyle={{ backgroundColor: '#2563EB' }}
                    icon={<FontAwesome6 name="arrow-rotate-right" size={20} color="white" />}
                    minWidth={responsive.isMobile ? 120 : 150}
                    {...Platform.select({
                      web: {
                        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                      }
                    })}
                  >
                    Try Again
                  </Button>
                </XStack>

                <SizableText
                  size="$2"
                  textAlign="center"
                  color="#6B7280"
                  marginTop={responsive.spacing.sm}
                  fontFamily="$mono"
                >
                  Error ID: {error.name}-{Date.now().toString(36)}
                </SizableText>
              </YStack>
            </View>
          </ScrollView>

          {this.renderFeedbackModal()}
        </View>
      );
    }

    return this.props.children;
  }
}

// Global error handlers setup
export const setupGlobalErrorHandlers = () => {
  if (Platform.OS === 'web') {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      // Report to crash interceptor if available
      if ((window as any).__CRASH_INTERCEPTOR_REPORT__) {
        const error = new Error(`Unhandled Promise Rejection: ${event.reason}`);
        (window as any).__CRASH_INTERCEPTOR_REPORT__(error);
      }
    });

    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      console.error('Uncaught error:', event.error);
      
      // Report to crash interceptor if available
      if ((window as any).__CRASH_INTERCEPTOR_REPORT__) {
        (window as any).__CRASH_INTERCEPTOR_REPORT__(event.error);
      }
    });
  }

  // React Native specific error handling
  if (Platform.OS !== 'web') {
    // Override console.error to catch React Native errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      originalConsoleError(...args);
      
      // Look for React errors
      const errorMessage = args.join(' ');
      if (errorMessage.includes('Error:') || errorMessage.includes('TypeError:')) {
        // This would need to be handled by the error boundary
        // as we can't directly trigger it from here
      }
    };
  }
};

// Function to detect dark theme without hooks
const detectDarkTheme = (): boolean => {
  if (Platform.OS === 'web') {
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return true;
    }
    
    // Check document body classes or styles
    const body = document.body;
    const classList = body.classList;
    if (classList.contains('dark') || classList.contains('dark-theme') || classList.contains('theme-dark')) {
      return true;
    }
    
    // Check computed styles
    const computedStyle = window.getComputedStyle(body);
    const backgroundColor = computedStyle.backgroundColor;
    if (backgroundColor && (backgroundColor.includes('rgb(0') || backgroundColor.includes('#000'))) {
      return true;
    }
  }
  
  return false; // Default to light theme
};

// Wrapper component that uses hooks and passes theme to class component
const CrashInterceptorWrapper: React.FC<Omit<Props, 'isDarkTheme'>> = (props) => {
  let isDarkTheme = false;
  
  try {
    const theme = useTheme();
    // Safely check theme properties with fallbacks
    const backgroundColor = theme?.background?.get?.() || theme?.backgroundColor?.get?.() || '#ffffff';
    isDarkTheme = backgroundColor === '#000000' || 
                  backgroundColor?.includes?.('dark') || 
                  backgroundColor?.includes?.('#000') ||
                  backgroundColor?.includes?.('#1') ||
                  backgroundColor?.includes?.('#2');
  } catch (error) {
    // If theme hook fails, fallback to system detection
    console.warn('Failed to get theme in CrashInterceptor, using system detection as fallback:', error);
    try {
      isDarkTheme = detectDarkTheme();
    } catch (fallbackError) {
      console.warn('System theme detection also failed, using light theme:', fallbackError);
      isDarkTheme = false;
    }
  }
  
  return <CrashInterceptor {...props} isDarkTheme={isDarkTheme} />;
};

// Safe version that doesn't use hooks - more reliable for error boundaries
const SafeCrashInterceptor: React.FC<Omit<Props, 'isDarkTheme'>> = (props) => {
  const isDarkTheme = detectDarkTheme();
  return <CrashInterceptor {...props} isDarkTheme={isDarkTheme} />;
};

export default SafeCrashInterceptor;
export { CrashInterceptor, CrashInterceptorWrapper };
